import {
  Button, IconInfo, SelectField, TextField, Tooltip,
} from 'gatorcomponents';
import React, { useCallback, useEffect, useState } from 'react';
import { BlockRuleInput } from '../BlockRuleInput';
import * as Styles from './CreateRule.styles';

export const CreateRule = ({
  locales,
  ruleForm,
  handleFormChange,
  rulesLeft,
  getSelectedOptionLabel,
  rulesOptions,
  getSelectedOptionValue,
  handleSubmitForm,
  isSubmitLoading,
  blockSubmit,
  submitBlockedUntil,
  ruleToEdit,
  actionOnSubmit,
}) => {
  const [blockedTime, setBlockedTime] = useState(0);

  const checkRuleFormChanged = () => {
    if (!ruleToEdit) {
      return true;
    }
    if (ruleToEdit.description !== ruleForm.description) {
      return true;
    }
    if (ruleToEdit.lock_type !== ruleForm.lockType) {
      return true;
    }
    if (ruleToEdit.value !== ruleForm.value) {
      return true;
    }
    return false;
  };

  const ruleFormHasChanges = checkRuleFormChanged();

  const setPreviousBlockedTime = useCallback(() => {
    const timestamp = submitBlockedUntil || 0;
    const currentTimestamp = Math.floor(Date.now() / 1000);

    if (timestamp > currentTimestamp) {
      setBlockedTime(timestamp - currentTimestamp);
    } else {
      setBlockedTime(0);
    }
  }, [submitBlockedUntil]);

  useEffect(() => {
    setPreviousBlockedTime();
  }, [setPreviousBlockedTime]);

  const updateBlockedTime = useCallback(() => {
    setBlockedTime((prevBlockedTime) => {
      if (prevBlockedTime <= 0) {
        return 0;
      }
      return prevBlockedTime - 1;
    });
  }, []);

  useEffect(() => {
    const intervalId = setInterval(updateBlockedTime, 1000);

    return () => clearInterval(intervalId);
  }, [updateBlockedTime]);

  const buttonDisabled = blockSubmit || !!blockedTime || !ruleFormHasChanges;

  const tooltipContent = () => {
    if (actionOnSubmit === 'edit' && blockedTime) {
      return locales.rules.editRuleBlockedUntil(blockedTime);
    }
    if (actionOnSubmit === 'create' && blockedTime) {
      return locales.rules.createRuleBlockedUntil(blockedTime);
    }
    if (!ruleFormHasChanges) {
      return locales.rules.noChanges;
    }
    return '';
  };

  return (
    <Styles.ContentWrapper>
      <Styles.Container>
        <Styles.Title>{locales.rules.title}</Styles.Title>
        <Styles.Description>
          {locales.rules.description}
        </Styles.Description>

        <Styles.BlockTextField>
          <TextField
            label={locales.rules.inputLabel}
            value={ruleForm.description}
            onChange={event => handleFormChange('description', event.target.value)}
            disabled={rulesLeft === 0}
            testId="cloudflare-rule-name__textfield"
          />
        </Styles.BlockTextField>

      </Styles.Container>

      <Styles.Container>
        <Styles.Title>{locales.traficBlock.title}</Styles.Title>
        <Styles.Description>
          <IconInfo
            color="primaryBlue"
            size="md"
            testId="IconInfo"
          />
          {rulesLeft > 0 ? locales.traficBlock.rulesLeftInfo(rulesLeft) : locales.traficBlock.noRulesLeftInfo()}
        </Styles.Description>

        <Styles.Description>{locales.traficBlock.description}</Styles.Description>
        <Styles.BlockTrafic>
          <Styles.SelectFieldContainer>
            <SelectField
              size="md"
              placeholder={locales.traficBlock.selectPlaceholder}
              value={getSelectedOptionLabel(rulesOptions, ruleForm.lockType)}
              options={rulesOptions}
              setValue={label => handleFormChange('lockType', getSelectedOptionValue(rulesOptions, label))}
              positionAbsolute
              disabled={rulesLeft === 0}
              testId="cloudflare-rule-type__select"
            />
          </Styles.SelectFieldContainer>

          <Styles.BlockRuleInputContainer>
            <Styles.BlockRuleInput>
              <BlockRuleInput
                selectedLockType={ruleForm.lockType}
                ruleValue={ruleForm.value}
                setRuleValue={value => handleFormChange('value', value)}
                locales={locales.traficBlock}
              />
            </Styles.BlockRuleInput>
            {ruleForm.lockType && (
            <Styles.ButtonContainer>
              <Tooltip
                content={tooltipContent()}
                disabled={!tooltipContent()}
                allowDynamicFit
              >
                <Button
                  label={locales.traficBlock.button}
                  size="medium2"
                  variant="primary"
                  loading={isSubmitLoading}
                  disabled={buttonDisabled}
                  onClick={handleSubmitForm}
                  width="full"
                  testId="cloudflare-add-rule-button"
                />
              </Tooltip>
            </Styles.ButtonContainer>
            )}
          </Styles.BlockRuleInputContainer>

        </Styles.BlockTrafic>
      </Styles.Container>
    </Styles.ContentWrapper>
  );
};

export default CreateRule;
